import { useEffect, useRef, type ReactNode } from 'react'
import { Link } from '@inertiajs/react'
import { Heading } from './Heading'
import { IconButton } from './IconButton'
import { CloseIcon } from './icons'
import styles from './Drawer.module.css'

export interface DrawerProps {
  opened: boolean
  onClose: () => void
  title: string
  children: ReactNode
}

/* Side sheet for the mobile navigation. */
export function Drawer({ opened, onClose, title, children }: DrawerProps) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (opened && !el.open) el.showModal?.()
    if (!opened && el.open) el.close()
  }, [opened])

  return (
    <dialog
      ref={ref}
      className={styles.drawer}
      aria-label={title}
      onCancel={(e) => { e.preventDefault(); onClose() }}
      onClick={(e) => { if (e.target === ref.current) onClose() }}
    >
      <div className={styles.head}>
        <Heading level={3}>{title}</Heading>
        <IconButton label="Close menu" icon={<CloseIcon />} size="sm" onClick={onClose} />
      </div>
      <div className={styles.body}>{children}</div>
    </dialog>
  )
}

export function DrawerSection({ children }: { children: ReactNode }) {
  return <div className={styles.section}>{children}</div>
}

export function DrawerDivider() {
  return <div className={styles.divider} role="separator" />
}

export interface DrawerItemProps {
  href?: string
  onClick?: () => void
  children: ReactNode
}

export function DrawerItem({ href, onClick, children }: DrawerItemProps) {
  if (href) return <Link href={href} className={styles.item} onClick={onClick}>{children}</Link>
  return <button type="button" className={styles.item} onClick={onClick}>{children}</button>
}
